import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import {
  CircularProgress,
  Switch,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useStyles } from '../../styles/styles';
import StyledCard from '../../components/cards/StyledCard';
import StyledCardContent from '../../components/cards/StyledCardContent';
import StyledCardHeader from '../../components/cards/StyledCardHeader';
import { drawerStore } from '../../state/drawerStore';
import { FormInputTypes } from '../../utils/constants';
import { Api } from '../../api';
import { greenColor } from '../../components/main/react/App';
import ActionsList from '../../components/other/ActionsList';
import {
  CHARGER_DETAILS_TABS,
  chargerDetailsTabParameters,
  priceListDetailsRoute,
} from '../../utils/routing';
import connectorIcon from '../../images/PricingIcons/connector_icon.png';
import { useProvider } from '../../state/providerStore';
import { getPriceListDropdownInput } from './LocationDetailsOverview';
import attachFileIcon from '../../images/PricingIcons/attach_file.png';
import accountBoxIcon from '../../images/PricingIcons/account_box.png';
import calendarClockIcon from '../../images/PricingIcons/calendar_clock.png';

const LocationDetailsPricing = observer(() => {
  const classes = useStyles();
  const history = useHistory();
  const { locationid } = useParams<{ locationid: string }>();
  const [connectors, setConnectors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const catalogsProvider = useProvider(Api.catalog.listCatalogs);

  // Lookup catalog details by ID
  const getCatalogInfo = (catalogId: string) => {
    let catalog = catalogsProvider.data?.find((c: any) => c.id === catalogId);
    if (!catalog) {
      for (const parent of catalogsProvider.data || []) {
        const child = parent.children?.find((c: any) => c.id === catalogId);
        if (child) {
          catalog = child;
          break;
        }
      }
    }
    return {
      name: catalog?.name || catalogId,
      productsCount: catalog?.productsCount ?? catalog?.products?.length ?? 0,
      isFree: catalog?.isFree || false,
      isUserRestricted: catalog?.isUserRestricted || false,
      isTimeRestricted: catalog?.isTimeRestricted || false,
    };
  };

  const loadPricing = async (showSpinner = true) => {
    try {
      if (showSpinner) {
        setLoading(true);
      }
      const { data } = await Api.charge.getConnectorsWithPricingForLocation(locationid);
      setConnectors(data?.data || data || []);
    } catch (err) {
      console.error('Failed to load pricing', err);
    } finally {
      setLoading(false);
    }
  };

  const updateConnectorLocally = (connectorId: string, updates: any) => {
    setConnectors((prev) => prev.map((cc) => (cc.id === connectorId ? { ...cc, ...updates } : cc)));
  };

  useEffect(() => {
    loadPricing();
  }, [locationid]);

  // Group connectors by charge point
  const chargePointGroups: Record<string, { chargePoint: any; connectors: any[] }> = {};
  connectors.forEach((cc: any) => {
    const cpId = cc.chargePointId || cc.chargePoint?.id;
    if (!chargePointGroups[cpId]) {
      chargePointGroups[cpId] = { chargePoint: cc.chargePoint, connectors: [] };
    }
    chargePointGroups[cpId].connectors.push(cc);
  });

  const navigateToChargePoint = (cpId: string) => {
    history.push(chargerDetailsTabParameters(cpId, CHARGER_DETAILS_TABS.OVERVIEW));
  };

  const onClickAssignPriceList = () => {
    // Build grouped connector list: { cpId, cpOcppId, connectors: [{id, ocppId}] }
    const groups: {
      cpId: string;
      cpOcppId: string;
      connectors: { id: string; ocppId: string }[];
    }[] = [];
    Object.entries(chargePointGroups).forEach(([cpId, group]) => {
      groups.push({
        cpId,
        cpOcppId: group.chargePoint?.ocppId || cpId,
        connectors: group.connectors.map((cc: any) => ({ id: cc.id, ocppId: cc.ocppId })),
      });
    });

    const allConnectorIds = connectors.map((cc: any) => cc.id);

    // ConnectorPicker rendered inside drawer via component type
    const ConnectorPicker = observer(() => {
      const selectedIds: string[] = toJS(drawerStore.values)?.selectedConnectorIds || [];
      const allSelected =
        allConnectorIds.length > 0 && allConnectorIds.every((id) => selectedIds.includes(id));

      const toggleConnector = (connectorId: string) => {
        const current: string[] = toJS(drawerStore.values)?.selectedConnectorIds || [];
        const next = current.includes(connectorId)
          ? current.filter((id) => id !== connectorId)
          : [...current, connectorId];
        drawerStore.setValues({ ...toJS(drawerStore.values), selectedConnectorIds: next });
      };

      const selectAll = () => {
        drawerStore.setValues({
          ...toJS(drawerStore.values),
          selectedConnectorIds: [...allConnectorIds],
        });
      };

      const removeConnector = (connectorId: string) => {
        const current: string[] = toJS(drawerStore.values)?.selectedConnectorIds || [];
        drawerStore.setValues({
          ...toJS(drawerStore.values),
          selectedConnectorIds: current.filter((id) => id !== connectorId),
        });
      };

      return (
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <span style={{ color: '#888', fontSize: 14 }}>Assign to</span>
            <Button
              variant="text"
              size="small"
              style={{ color: greenColor, fontWeight: 600, textTransform: 'none', fontSize: 13 }}
              onClick={selectAll}
            >
              Select all
            </Button>
          </div>

          {groups.map((group) => (
            <div key={group.cpId} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 4 }}>
                {group.cpOcppId}
              </div>
              {group.connectors.map((cc) => (
                <FormControlLabel
                  key={cc.id}
                  control={
                    <Checkbox
                      checked={selectedIds.includes(cc.id)}
                      onChange={() => toggleConnector(cc.id)}
                      sx={{ '&.Mui-checked': { color: greenColor } }}
                      size="small"
                    />
                  }
                  label={<span style={{ fontSize: 14 }}>{cc.ocppId}</span>}
                  style={{ display: 'flex', marginLeft: 0 }}
                />
              ))}
            </div>
          ))}

          {selectedIds.length > 0 && (
            <div style={{ borderTop: '1px solid #eee', paddingTop: 8, marginTop: 8 }}>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>
                Selected ({selectedIds.length})
              </div>
              {selectedIds.map((id) => {
                const cc = connectors.find((c: any) => c.id === id);
                return (
                  <div
                    key={id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '4px 0',
                    }}
                  >
                    <span style={{ fontSize: 14 }}>{cc?.ocppId || id}</span>
                    <IconButton size="small" onClick={() => removeConnector(id)}>
                      <CloseIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    });

    drawerStore.openDrawer({
      label: 'Assign Price list',
      inputs: [
        {
          name: 'isFree',
          label: 'Is Free',
          type: FormInputTypes.switch,
        },
        getPriceListDropdownInput(catalogsProvider, []),
        {
          name: 'connectorPicker',
          label: '',
          type: FormInputTypes.component,
        },
      ],
      clickCallback: async () => {
        const values = toJS(drawerStore.values);
        const selectedConnectorIds: string[] = values.selectedConnectorIds || [];

        if (values.isFree && selectedConnectorIds.length > 0) {
          for (const connectorId of selectedConnectorIds) {
            await Api.charge.updateConnector(connectorId, { isFree: true });
          }
        }

        if (values.catalogId && selectedConnectorIds.length > 0) {
          await Api.charge.batchAddCatalogToConnectors(selectedConnectorIds, values.catalogId);
        }

        loadPricing();
      },
      buttonLabel: 'ASSIGN',
    });

    // Set initial values with the component
    drawerStore.setValues({
      isFree: false,
      catalogId: '',
      selectedConnectorIds: [],
      connectorPicker: <ConnectorPicker />,
    });
  };

  const formatPower = (cp: any) => {
    const watts = parseInt(cp?.actualMaxPower || cp?.maxPower || '0', 10);
    return watts >= 1000 ? `${(watts / 1000).toFixed(0)} kW` : `${watts} W`;
  };

  const renderConnectorCell = (cc: any, rowSpan: number) => (
    <td rowSpan={rowSpan} style={{ padding: '8px', verticalAlign: 'top' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <img src={connectorIcon} alt="Connector" style={{ width: 24, height: 24 }} />
        <div>
          <div
            style={{ color: greenColor, fontWeight: 500, cursor: 'pointer' }}
            onClick={() => navigateToChargePoint(cc.chargePointId || cc.chargePoint?.id)}
          >
            {cc.ocppId}
          </div>
          <div style={{ fontSize: 12, color: '#888' }}>
            {cc.name} ({cc.type})/{formatPower(cc.chargePoint)}
          </div>
        </div>
      </div>
    </td>
  );

  const renderIsFreeCell = (cc: any, rowSpan: number) => (
    <td rowSpan={rowSpan} style={{ textAlign: 'center', verticalAlign: 'top', padding: '8px' }}>
      <Switch
        checked={cc.isFree || false}
        onChange={async (e) => {
          await Api.charge.updateConnector(cc.id, { isFree: e.target.checked });
          updateConnectorLocally(cc.id, { isFree: e.target.checked });
        }}
        sx={{
          '& .MuiSwitch-switchBase.Mui-checked': { color: greenColor },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: greenColor,
          },
        }}
      />
    </td>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <CircularProgress style={{ color: greenColor }} />
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <Button
          variant="text"
          size="small"
          style={{ color: greenColor, fontWeight: 600, textTransform: 'none' }}
          onClick={onClickAssignPriceList}
        >
          ASSIGN PRICE LIST
        </Button>
      </div>

      {Object.entries(chargePointGroups).map(([cpId, group]) => (
        <div key={cpId} style={{ marginBottom: '1.5rem' }}>
          <StyledCard>
            <div
              style={{ padding: '16px 16px 8px', display: 'flex', alignItems: 'baseline', gap: 8 }}
            >
              <span style={{ fontSize: '1.1rem' }}>Charge point</span>
              <span style={{ fontSize: 14, color: '#888' }}>
                (ID:{' '}
                <span
                  style={{ color: greenColor, cursor: 'pointer', fontWeight: 500 }}
                  onClick={() => navigateToChargePoint(cpId)}
                >
                  {group.chargePoint?.ocppId || cpId}
                </span>
                )
              </span>
            </div>
            <StyledCardContent>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #eee', color: '#888', fontSize: 13 }}>
                    <th style={{ textAlign: 'left', padding: '8px', fontWeight: 400 }}>OCPP ID</th>
                    <th style={{ textAlign: 'center', padding: '8px', fontWeight: 400, width: 80 }}>
                      Is Free
                    </th>
                    <th style={{ textAlign: 'left', padding: '8px', fontWeight: 400 }}>
                      Price Lists
                    </th>
                    <th />
                    <th style={{ width: 40 }} />
                  </tr>
                </thead>
                <tbody>
                  {group.connectors.map((cc: any) => {
                    const catalogs = cc.catalogs || [];
                    const rowCount = Math.max(catalogs.length, 1);

                    return catalogs.length > 0 ? (
                      catalogs.map((cat: any, idx: number) => (
                        <tr key={`${cc.id}-${idx}`} style={{ borderBottom: '1px solid #f5f5f5' }}>
                          {idx === 0 && (
                            <>
                              {renderConnectorCell(cc, rowCount)}
                              {renderIsFreeCell(cc, rowCount)}
                            </>
                          )}
                          <td style={{ padding: '8px' }}>
                            <span style={{ color: '#888', marginRight: 8 }}>
                              {catalogs.length - idx}.
                            </span>
                            <div style={{ display: 'inline-block' }}>
                              <div
                                style={{
                                  color: greenColor,
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                }}
                                onClick={() => history.push(priceListDetailsRoute(cat.catalogId))}
                              >
                                {getCatalogInfo(cat.catalogId).name}
                              </div>
                              <div style={{ fontSize: 12, color: '#888' }}>
                                {getCatalogInfo(cat.catalogId).productsCount} products
                              </div>
                            </div>
                          </td>
                          <td
                            style={{
                              padding: '8px',
                              whiteSpace: 'nowrap',
                              verticalAlign: 'middle',
                            }}
                          >
                            {getCatalogInfo(cat.catalogId).isFree && (
                              <img
                                src={attachFileIcon}
                                alt="Attachment"
                                style={{ width: 20, height: 20, marginRight: 6 }}
                              />
                            )}
                            {getCatalogInfo(cat.catalogId).isUserRestricted && (
                              <img
                                src={accountBoxIcon}
                                alt="User restricted"
                                style={{ width: 20, height: 20, marginRight: 6 }}
                              />
                            )}
                            {getCatalogInfo(cat.catalogId).isTimeRestricted && (
                              <img
                                src={calendarClockIcon}
                                alt="Time restricted"
                                style={{ width: 20, height: 20, marginRight: 6 }}
                              />
                            )}
                          </td>
                          <td style={{ padding: '8px' }}>
                            <ActionsList
                              actions={[
                                {
                                  label: 'Remove',
                                  callback: async () => {
                                    await Api.charge.batchDeleteCatalogFromConnectors(
                                      [cc.id],
                                      cat.catalogId,
                                    );
                                    loadPricing();
                                  },
                                },
                              ]}
                            />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr key={cc.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                        {renderConnectorCell(cc, 1)}
                        {renderIsFreeCell(cc, 1)}
                        <td style={{ padding: '8px', color: '#aaa', fontStyle: 'italic' }}>
                          No price lists assigned
                        </td>
                        <td />
                        <td />
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </StyledCardContent>
          </StyledCard>
        </div>
      ))}

      {Object.keys(chargePointGroups).length === 0 && (
        <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>
          No charge points or connectors found for this location.
        </div>
      )}
    </div>
  );
});

export default LocationDetailsPricing;
